'use client';

import { WhiteTruck } from '@c/cards/MXListProductCard';
import EstimateShippingModal from '@c/modals/EstimateShippingModal';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';
import { formatCurrency } from '@util/index';
import { ChevronDownIcon, RefreshCwIcon } from 'lucide-react';
import { ProductDocument, Variation } from 'models/product';
import { useState } from 'react';

const ProductPriceAndShipping = ({
  product,
  selectedVariations,
  shipping,
  customInputsEnabled,
}: {
  product: ProductDocument;
  selectedVariations?: Variation[] | null;
  shipping?: {
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null;
  customInputsEnabled?: boolean;
}) => {
  const { highestBid } = useRealTimeBids(product.id);
  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const [estimateShippingOpen, setEstimateShippingOpen] = useState(false);
  const [estimatedShipping, setEstimatedShipping] = useState<{
    code: string;
    cost: number;
    name: string;
    estimated?: boolean;
  } | null>(null);
  const getPrice = () => {
    if (product.is_auction) {
      return highestBid?.bid_price || product.price;
    }
    let basePrice = product.price;
    if (selectedVariations) {
      const letterSize = selectedVariations?.find((v) => isNaN(+v.size));
      const numberSize = selectedVariations?.find(
        (v) => !isNaN(+v.size) && v.is_number
      );
      const isColor = selectedVariations?.find((v) => !!v.color);
      if (letterSize && numberSize && !isColor) {
        basePrice = letterSize.price + numberSize.price;
      }
      if (letterSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === letterSize.size
            )?.price || letterSize.price;
        }
        basePrice = letterSize.price;
      }
      if (numberSize) {
        if (isColor) {
          basePrice =
            product.variations?.find(
              (v) => v.color === isColor.color && v.size === numberSize.size
            )?.price || numberSize.price;
        }
        basePrice = numberSize.price;
      }
    }

    if (customInputsEnabled && product.custom_inputs) {
      basePrice += product.custom_inputs.cost;
    }

    return basePrice;
  };
  const displayedShipping = estimatedShipping ?? shipping;

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex items-center gap-x-4">
        <p className="text-[3rem] font-semibold leading-[2.4rem] text-brand-primary">
          {formatCurrency(getPrice())}
        </p>

        {product.category !== 'Dirt Bikes' && (
          <>
            {displayedShipping ? (
              <>
                {product.is_flat_rate &&
                product.shipping_costs?.every(({ cost }) => cost === 0) &&
                !product.is_digital ? (
                  <div className="rounded-2xl bg-black px-4 py-2">
                    <div className="flex items-center gap-[.5rem]">
                      <WhiteTruck className={'h-9 w-9'} />

                      <span className="text-[1.6rem] font-semibold italic text-white">
                        Free Shipping
                      </span>
                    </div>
                  </div>
                ) : !product.is_digital ? (
                  <div className="relative flex items-center gap-x-2">
                    <div className="flex items-center gap-x-2 text-[1.6rem]">
                      + {formatCurrency(displayedShipping.cost)} Shipping (
                      {displayedShipping.code})
                    </div>
                    {!displayedShipping.estimated ? (
                      <button
                        type="button"
                        className="border-none bg-inherit"
                        aria-label="Show shipping options"
                        onClick={() =>
                          setShowShippingOptions(!showShippingOptions)
                        }
                      >
                        <ChevronDownIcon className="h-8 w-8 text-zinc-700" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="border-none bg-inherit"
                        aria-label="Redo estimate"
                        onClick={() => setEstimateShippingOpen(true)}
                      >
                        <RefreshCwIcon className="h-8 w-8 text-zinc-700" />
                      </button>
                    )}

                    {showShippingOptions && (
                      <div className="absolute right-0 top-full z-10 w-full rounded-xl bg-white px-4 py-2 shadow-lg ">
                        {product.shipping_costs?.map(({ code, cost }) => (
                          <div
                            className="flex items-center gap-x-2 py-2 text-[1.6rem]"
                            key={code}
                          >
                            + {formatCurrency(cost)} Shipping ({code})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </>
            ) : displayedShipping === null ? (
              <>
                <button
                  type="button"
                  className="underline"
                  onClick={() => setEstimateShippingOpen(true)}
                >
                  Estimate Shipping
                </button>
              </>
            ) : (
              <div className="h-6 w-48 animate-pulse rounded-xl bg-brand-lightest-gray" />
            )}
          </>
        )}
      </div>

      {product.on_sale && (
        <div className="flex items-center gap-x-2">
          <p className="text-[1.6rem] font-semibold text-zinc-600">
            Was{' '}
            <span className="text-[1.6rem] line-through">
              {formatCurrency(product.og_price)}
            </span>
          </p>

          <p className="font-bold text-brand-secondary">
            (
            {Math.round(
              ((Number(product.og_price) - product.price) /
                Number(product.og_price)) *
                100
            )}
            % off)
          </p>
        </div>
      )}
      <EstimateShippingModal
        product_id={product.id}
        isOpen={estimateShippingOpen}
        dismiss={(shippingCost) => {
          setEstimatedShipping(shippingCost);
          setEstimateShippingOpen(false);
        }}
        key={estimateShippingOpen.toString()}
      />
    </div>
  );
};

export default ProductPriceAndShipping;
